import { normalizeHistory } from "./previousPerformance";
import {
  appendActivityEvent,
  normalizeActiveDuration,
  resolveFinishTiming,
} from "./activeDuration";
import { evaluatePerformedCardio, evaluatePerformedSet } from "./performedRules";
import {
  dismissRestTimer,
  extendRestTimer,
  REST_TIMER_MAX_SECONDS,
  REST_TIMER_SOURCES,
  startRestTimer,
} from "./restTimer";
import { isSetPatchNoOp } from "./setFacts";
import {
  compatibleCardioGhostMinutes,
  effectiveExecutionProfile,
  itemOwnedExecutionProfile,
} from "./effectiveExecutionProfile";
import {
  assertSubstitutionAllowed,
  buildSubstitutionReplacement,
  createUnmappedPlaceholder,
  isUnmappedPlaceholder,
  SUBSTITUTION_ERRORS,
  SubstitutionError,
  substitutionWouldDiscardDraftFacts,
} from "./substitution";
import { createClientUuid } from "./clientIds";
import {
  normalizeExerciseNotes,
  personalNoteFor,
  rememberExerciseNote,
} from "./exerciseNotes";
import {
  MAX_EXECUTION_ITEMS,
  MAX_SETS_PER_ITEM,
} from "./executionLimits";

export const EXECUTION_ACTIONS = Object.freeze({
  COMMIT_SET: "COMMIT_SET",
  COMPLETE_CARDIO: "COMPLETE_CARDIO",
  NAVIGATE_EXERCISE: "NAVIGATE_EXERCISE",
  ADD_ITEM: "ADD_ITEM",
  REMOVE_ITEM: "REMOVE_ITEM",
  SUBSTITUTE_ITEM: "SUBSTITUTE_ITEM",
  SET_ITEM_SKIPPED: "SET_ITEM_SKIPPED",
  ADD_SET: "ADD_SET",
  REMOVE_SET: "REMOVE_SET",
  COMMIT_NOTES: "COMMIT_NOTES",
  START_REST_TIMER: "START_REST_TIMER",
  EXTEND_REST_TIMER: "EXTEND_REST_TIMER",
  DISMISS_REST_TIMER: "DISMISS_REST_TIMER",
  DISMISS_REST_OFFER: "DISMISS_REST_OFFER",
  FINISH: "FINISH",
  SAVE: "SAVE",
  DISCARD: "DISCARD",
  CONTINUE_EDITING: "CONTINUE_EDITING",
});

function isoNow(value) {
  const date = value == null ? new Date() : new Date(value);
  if (Number.isNaN(date.getTime())) throw new TypeError("The action timestamp is invalid.");
  return date.toISOString();
}

function assertSetContract(sets, label) {
  if (sets.length > MAX_SETS_PER_ITEM) {
    throw new RangeError(`${label} cannot contain more than ${MAX_SETS_PER_ITEM} sets.`);
  }
  const indices = new Set();
  sets.forEach((set) => {
    if (!Number.isInteger(set.setIndex) || set.setIndex < 1 || set.setIndex > MAX_SETS_PER_ITEM) {
      throw new RangeError(`setIndex must be from 1 to ${MAX_SETS_PER_ITEM}.`);
    }
    if (indices.has(set.setIndex)) throw new Error(`${label} contains duplicate setIndex values.`);
    indices.add(set.setIndex);
  });
}

function orderByNumericField(values, field) {
  return [...values].sort((left, right) => left[field] - right[field]);
}

export function createInitialExecutionItems(
  snapshot,
  clientStartedAt,
  uuidFactory = createClientUuid,
  exerciseNotes = {}
) {
  const exerciseCount = (snapshot?.blocks || []).reduce(
    (total, block) => total + (block.exercises || []).length,
    0
  );
  if (exerciseCount > MAX_EXECUTION_ITEMS) {
    throw new RangeError(`An execution cannot contain more than ${MAX_EXECUTION_ITEMS} items.`);
  }
  let position = 0;
  return (snapshot?.blocks || []).flatMap((block) =>
    (block.exercises || []).map((exercise) => {
      position += 1;
      // An unmapped Weekly Plan slot is a local-only placeholder until an
      // exercise is chosen for it (§15.8); it is never synced or logged.
      if (!exercise.exerciseId) {
        return createUnmappedPlaceholder({ block, exercise, position, itemId: uuidFactory() });
      }
      assertSetContract(exercise.sets || [], `Exercise ${exercise.exerciseKey || position}`);
      const cardio = exercise.profile?.measurementType === "CARDIO_MINUTES"
        ? { minutes: null, completed: false, completedAt: null }
        : null;
      return {
        itemId: uuidFactory(),
        position,
        blockKey: block.blockKey,
        laneIndex: exercise.laneIndex ?? null,
        origin: "PRESCRIBED",
        prescribedExerciseKey: exercise.exerciseKey,
        substitutedFromExerciseId: null,
        exerciseId: exercise.exerciseId,
        exerciseName: exercise.exerciseName,
        measurementType: exercise.profile?.measurementType,
        loadMode: exercise.profile?.loadMode,
        suggestedFamily: exercise.profile?.suggestedFamily || null,
        executionProfile: exercise.profile || null,
        // The exercise note carried from saved history, when there is one.
        notes: personalNoteFor({ exerciseNotes }, exercise.exerciseId),
        skipped: false,
        cardio,
        sets: (exercise.sets || []).map((set) => ({
          setId: uuidFactory(),
          setIndex: set.setIndex,
          origin: "PRESCRIBED",
          prescribedSetKey: set.setKey,
          setType: set.setType || "WORKING",
          load: null,
          reps: null,
          seconds: null,
          rir: null,
          performedAt: null,
          lastEditedAt: clientStartedAt,
          enteredAfterFinish: false,
        })),
      };
    })
  );
}

function mapItem(document, itemId, mapper) {
  let found = false;
  const items = (document.items || []).map((item) => {
    if (item.itemId !== itemId) return item;
    found = true;
    return mapper(item);
  });
  if (!found) throw new Error(`Unknown workout item ${itemId}`);
  return { ...document, items };
}

function mapSet(document, itemId, setId, mapper) {
  return mapItem(document, itemId, (item) => {
    let found = false;
    const sets = (item.sets || []).map((set) => {
      if (set.setId !== setId) return set;
      found = true;
      return mapper(set, item);
    });
    if (!found) throw new Error(`Unknown workout set ${setId}`);
    return { ...item, sets };
  });
}

function addActivity(record, eventAt) {
  if (record.document?.status !== "ACTIVE") return record.timing;
  return {
    ...record.timing,
    ...appendActivityEvent({
      activeSegments: record.timing?.activeSegments || [],
      lastActivityAt: record.timing?.lastActivityAt,
      eventAt,
    }),
  };
}

function assertRestActive(record) {
  if (record.document.status !== "ACTIVE") throw new Error("Rest runs only during an ACTIVE workout.");
}

function revised(record, { document = record.document, timing = record.timing,
  restTimer = record.restTimer, restOffer = record.restOffer ?? null } = {}) {
  const localRevision = Number(record.localRevision || 0) + 1;
  return {
    ...record,
    document,
    timing,
    restTimer,
    restOffer,
    status: document.status,
    localRevision,
    pending: localRevision > Number(record.ackedRevision || 0),
    syncState: "PENDING",
    lastSyncError: null,
  };
}

// The server never holds a placeholder: every unmapped snapshot slot that no
// item answers gets exactly one local placeholder again, at its canonical
// position (or the next free one if that position is taken).
function withUnmappedPlaceholders(snapshot, items, uuidFactory) {
  const answeredKeys = new Set(items.map((item) => item.prescribedExerciseKey).filter(Boolean));
  const taken = new Set(items.map((item) => item.position));
  const placeholders = [];
  let position = 0;
  (snapshot?.blocks || []).forEach((block) => {
    (block.exercises || []).forEach((exercise) => {
      position += 1;
      if (exercise.exerciseId || answeredKeys.has(exercise.exerciseKey)) return;
      let slotPosition = position;
      if (taken.has(slotPosition)) slotPosition = Math.max(0, ...taken) + 1;
      taken.add(slotPosition);
      placeholders.push(createUnmappedPlaceholder({
        block,
        exercise,
        position: slotPosition,
        itemId: uuidFactory(),
      }));
    });
  });
  return placeholders.length
    ? orderByNumericField([...items, ...placeholders], "position")
    : items;
}

function assertLoggableItem(record, item) {
  if (isUnmappedPlaceholder(record, item)) {
    throw new Error("Choose an exercise for this slot before logging it.");
  }
}

export function createLocalExecutionRecord({
  userId,
  deviceId,
  execution,
  history = null,
  exerciseNotes = null,
  clientStartedAt,
  uuidFactory = createClientUuid,
} = {}) {
  if (!userId || !deviceId || !execution?.executionId) {
    throw new TypeError("userId, deviceId and execution are required");
  }
  const started = isoNow(clientStartedAt || execution.startedAt);
  const knownNotes = normalizeExerciseNotes(exerciseNotes);
  const server = {
    startedAt: execution.startedAt,
    trainingDate: execution.trainingDate,
    title: execution.title,
    sourceKind: execution.sourceKind,
    contextKey: execution.contextKey,
    intendedOccurrence: execution.intendedOccurrence || null,
    projection: execution.projection || null,
    policy: execution.policy || null,
    snapshot: execution.snapshot,
    // LAST / Suggested inputs (§13.1). Server-provided context: storing it is
    // never a document revision. An absent history reads as empty maps.
    history: normalizeHistory(history),
    serverRevision: execution.serverRevision,
  };
  return {
    recordSchemaVersion: 1,
    executionId: execution.executionId,
    userId,
    deviceId,
    leaseEpoch: execution.leaseEpoch,
    localRevision: 0,
    ackedRevision: 0,
    pending: false,
    syncState: "SYNCED",
    server,
    // This workout's exercise-note drafts (exerciseNotes.js). Local context:
    // never synced, and storing it is never a document revision.
    exerciseNotes: knownNotes,
    document: {
      status: execution.status || "ACTIVE",
      items: createInitialExecutionItems(
        execution.snapshot,
        started,
        uuidFactory,
        knownNotes
      ),
      notes: null,
      finishedAt: null,
      workoutEndedAt: null,
      finishTimezone: null,
      savedAt: null,
      finalizedBy: null,
      discardedAt: null,
    },
    timing: {
      clientStartedAt: started,
      activeSegments: [[started, started]],
      lastActivityAt: started,
    },
    restTimer: null,
    restOffer: null,
    result: null,
    lastSyncError: null,
  };
}

export function createHydratedLocalExecutionRecord({
  userId,
  deviceId,
  execution,
  document,
  history = null,
  exerciseNotes = null,
  uuidFactory = createClientUuid,
} = {}) {
  if (!userId || !deviceId || !execution?.executionId || !document) {
    throw new TypeError("userId, deviceId, execution and document are required");
  }
  const knownNotes = normalizeExerciseNotes(exerciseNotes);
  const revision = Number(execution.clientRevision);
  if (!Number.isSafeInteger(revision) || revision < 0) {
    throw new TypeError("execution.clientRevision must be a non-negative integer");
  }
  const {
    lastActivityAt,
    activeSegments,
    ...executionDocument
  } = document;
  const snapshotProfiles = new Map();
  (execution.snapshot?.blocks || []).forEach((block) => {
    (block.exercises || []).forEach((exercise) => {
      snapshotProfiles.set(exercise.exerciseKey, exercise.profile || null);
    });
  });
  const serverItems = executionDocument.items || [];
  const hydratedItems = revision === 0 && serverItems.length === 0
    ? createInitialExecutionItems(
        execution.snapshot,
        execution.clientStartedAt || activeSegments?.[0]?.[0] || execution.startedAt,
        uuidFactory,
        knownNotes
      )
    : serverItems;
  const answered = hydratedItems.map((item) => {
    const sets = (item.sets || []).map((set) => {
      const mutableSet = { ...set };
      delete mutableSet.performed;
      return mutableSet;
    });
    return {
      ...item,
      sets,
      // Ownership follows origin (§15.8). A PRESCRIBED item keeps a
      // convenience copy of its snapshot profile; the snapshot stays the
      // authority. An ADDED or SUBSTITUTED item keeps the profile the server
      // froze on first acceptance, never the snapshot of the answered slot;
      // only legacy rows without one fall back to their stored core.
      executionProfile: String(item.origin || "").toUpperCase() === "PRESCRIBED"
        ? snapshotProfiles.get(item.prescribedExerciseKey) || null
        : itemOwnedExecutionProfile(item),
    };
  });
  const items = withUnmappedPlaceholders(execution.snapshot, answered, uuidFactory);
  return {
    recordSchemaVersion: 1,
    executionId: execution.executionId,
    userId,
    deviceId,
    leaseEpoch: execution.leaseEpoch,
    localRevision: revision,
    ackedRevision: revision,
    pending: false,
    syncState: "SYNCED",
    status: executionDocument.status || execution.status,
    server: {
      startedAt: execution.startedAt,
      trainingDate: execution.trainingDate,
      title: execution.title,
      sourceKind: execution.sourceKind,
      contextKey: execution.contextKey,
      intendedOccurrence: execution.intendedOccurrence || null,
      projection: execution.projection || null,
      policy: execution.policy || null,
      snapshot: execution.snapshot,
      history: normalizeHistory(history),
      serverRevision: execution.serverRevision,
    },
    // The server's notes, then the ones the stored items already carry.
    exerciseNotes: items.reduce(
      (notes, item) => (item.exerciseId && item.notes ? { ...notes, [item.exerciseId]: item.notes } : notes),
      knownNotes
    ),
    document: { ...executionDocument, items },
    timing: {
      clientStartedAt: execution.clientStartedAt || activeSegments?.[0]?.[0] || execution.startedAt,
      activeSegments: activeSegments || [],
      lastActivityAt: lastActivityAt || execution.startedAt,
    },
    restTimer: null,
    restOffer: null,
    result: null,
    lastSyncError: null,
  };
}

export function executionReducer(record, action = {}) {
  const at = isoNow(action.at);
  switch (action.type) {
    case EXECUTION_ACTIONS.COMMIT_SET: {
      let unchanged = false;
      const nextDocument = mapSet(record.document, action.itemId, action.setId, (set, item) => {
        const profile = effectiveExecutionProfile(record, item);
        if (!profile) throw new Error("The item's frozen execution profile is required.");
        const patch = action.patch || {};
        const candidate = { ...set, ...patch };
        // Validate before accepting a no-op: an invalid value still throws.
        const result = evaluatePerformedSet({
          measurementType: profile.measurementType,
          loadMode: profile.loadMode,
          set: candidate,
          rirInputEnabled: record.server?.policy?.rirInputEnabled !== false,
          previousPerformedAt: set.performedAt,
        });
        if (!result.valid) throw new TypeError("The committed performed value is invalid.");
        if (isSetPatchNoOp(set, patch)) {
          unchanged = true;
          return set;
        }
        return {
          ...candidate,
          lastEditedAt: at,
          enteredAfterFinish: set.enteredAfterFinish || record.document.status === "FINISHED",
          performedAt: set.performedAt || (result.performed ? at : null),
        };
      });
      // A semantic no-op returns the original record: no revision, no
      // metadata stamp, no activity (§9.4).
      if (unchanged) return record;
      return revised(record, { document: nextDocument, timing: addActivity(record, at) });
    }

    case EXECUTION_ACTIONS.COMPLETE_CARDIO: {
      // The prescribed ghost is accepted only by a first completion. Blank
      // minutes on an already-completed item are not an update (§9.4 no-op).
      const target = (record.document.items || []).find((item) => item.itemId === action.itemId);
      if (target?.cardio?.completed === true && action.minutes == null) return record;
      const nextDocument = mapItem(record.document, action.itemId, (item) => {
        assertLoggableItem(record, item);
        const result = evaluatePerformedCardio({
          cardio: { minutes: action.minutes ?? null, completed: true },
          cardioGhostMinutes: compatibleCardioGhostMinutes(record, item),
        });
        if (!result.valid) throw new TypeError("The committed cardio value is invalid.");
        return {
          ...item,
          cardio: {
            minutes: result.minutes,
            completed: true,
            completedAt: item.cardio?.completedAt || at,
          },
        };
      });
      if (record.document.status === "FINISHED") {
        return revised(record, { document: nextDocument });
      }
      return revised(record, {
        document: nextDocument,
        timing: addActivity(record, at),
      });
    }

    case EXECUTION_ACTIONS.NAVIGATE_EXERCISE:
      return revised(record, { timing: addActivity(record, at) });

    case EXECUTION_ACTIONS.ADD_ITEM: {
      const items = record.document.items || [];
      if (items.length >= MAX_EXECUTION_ITEMS) {
        throw new RangeError(`An execution cannot contain more than ${MAX_EXECUTION_ITEMS} items.`);
      }
      if (
        !Number.isInteger(action.item?.position) ||
        action.item.position < 1 ||
        action.item.position > MAX_EXECUTION_ITEMS
      ) {
        throw new RangeError(`Item position must be from 1 to ${MAX_EXECUTION_ITEMS}.`);
      }
      if (items.some((item) => item.position === action.item.position)) {
        throw new Error("Execution item positions must be unique.");
      }
      assertSetContract(action.item.sets || [], "An execution item");
      // An added exercise starts with its carried or drafted note.
      const added = {
        ...action.item,
        notes: personalNoteFor(record, action.item.exerciseId, action.item.notes),
      };
      return {
        ...revised(record, {
          document: {
            ...record.document,
            items: orderByNumericField([...items, added], "position"),
          },
          timing: addActivity(record, at),
        }),
        exerciseNotes: rememberExerciseNote(record, added.exerciseId, added.notes),
      };
    }

    case EXECUTION_ACTIONS.REMOVE_ITEM: {
      const item = (record.document.items || []).find((candidate) => candidate.itemId === action.itemId);
      if (!item) throw new Error(`Unknown workout item ${action.itemId}`);
      if (item.prescribedExerciseKey != null) {
        throw new Error("Prescription-backed exercises cannot be removed.");
      }
      return revised(record, {
        document: {
          ...record.document,
          items: (record.document.items || []).filter((item) => item.itemId !== action.itemId),
        },
        timing: addActivity(record, at),
      });
    }

    // §15.8: a substitution, re-substitution or restore replaces the item
    // answering a prescribed slot with a NEW item built from the current item,
    // the immutable snapshot and the chosen exercise. One revision, one
    // activity event; rest state is untouched and no anchor can arise.
    case EXECUTION_ACTIONS.SUBSTITUTE_ITEM: {
      const items = record.document.items || [];
      const item = items.find((candidate) => candidate.itemId === action.itemId);
      if (!item) throw new Error(`Unknown workout item ${action.itemId}`);
      const target = action.target || {};
      const { noop } = assertSubstitutionAllowed(record, item, target);
      if (noop) return record;
      if (!action.confirmDiscard && substitutionWouldDiscardDraftFacts(item)) {
        throw new SubstitutionError(
          SUBSTITUTION_ERRORS.CONFIRM_DISCARD_REQUIRED,
          "Substituting would discard the values entered for this exercise."
        );
      }
      // The note the leaving exercise carries is remembered first, so it
      // comes back with that exercise (also for a record started before this
      // memory existed).
      const known = {
        ...record,
        exerciseNotes: rememberExerciseNote(record, item.exerciseId, item.notes),
      };
      const replacement = buildSubstitutionReplacement(known, item, target, {
        itemId: action.ids?.itemId,
        setIds: action.ids?.setIds,
        at,
      });
      const usedIds = new Set(items.flatMap((candidate) => [
        candidate.itemId,
        ...(candidate.sets || []).map((set) => set.setId),
      ]));
      if ([replacement.itemId, ...replacement.sets.map((set) => set.setId)].some((id) => usedIds.has(id))) {
        throw new SubstitutionError(SUBSTITUTION_ERRORS.INVALID_IDS, "Substitution ids must be new.");
      }
      return {
        ...revised(record, {
          document: {
            ...record.document,
            items: items.map((candidate) => (candidate.itemId === item.itemId ? replacement : candidate)),
          },
          timing: addActivity(record, at),
        }),
        exerciseNotes: rememberExerciseNote(known, replacement.exerciseId, replacement.notes),
      };
    }

    case EXECUTION_ACTIONS.SET_ITEM_SKIPPED:
      return revised(record, {
        document: mapItem(record.document, action.itemId, (item) => {
          assertLoggableItem(record, item);
          const profile = effectiveExecutionProfile(record, item);
          const hasPerformedWork = Boolean(item.cardio?.completed) ||
            (item.sets || []).some((set) => evaluatePerformedSet({
              measurementType: profile?.measurementType,
              loadMode: profile?.loadMode,
              set,
              rirInputEnabled: record.server?.policy?.rirInputEnabled !== false,
            }).performed);
          if (action.skipped && hasPerformedWork) {
            throw new Error("A performed exercise cannot be skipped.");
          }
          return { ...item, skipped: Boolean(action.skipped) };
        }),
        timing: addActivity(record, at),
      });

    case EXECUTION_ACTIONS.ADD_SET:
      return revised(record, {
        document: mapItem(record.document, action.itemId, (item) => {
          assertLoggableItem(record, item);
          const sets = item.sets || [];
          if (sets.length >= MAX_SETS_PER_ITEM) {
            throw new RangeError(`An execution item cannot contain more than ${MAX_SETS_PER_ITEM} sets.`);
          }
          if (
            !Number.isInteger(action.set?.setIndex) ||
            action.set.setIndex < 1 ||
            action.set.setIndex > MAX_SETS_PER_ITEM
          ) {
            throw new RangeError(`setIndex must be from 1 to ${MAX_SETS_PER_ITEM}.`);
          }
          if (sets.some((set) => set.setIndex === action.set.setIndex)) {
            throw new Error("Execution set indices must be unique per item.");
          }
          return {
            ...item,
            sets: orderByNumericField([...sets, action.set], "setIndex"),
          };
        }),
        timing: addActivity(record, at),
      });

    case EXECUTION_ACTIONS.REMOVE_SET:
      return revised(record, {
        document: mapItem(record.document, action.itemId, (item) => ({
          ...item,
          sets: (item.sets || []).filter((set) => {
            if (set.setId !== action.setId) return true;
            if (set.origin !== "ADDED") {
              throw new Error("Prescribed sets cannot be removed.");
            }
            return false;
          }),
        })),
        timing: addActivity(record, at),
      });

    case EXECUTION_ACTIONS.COMMIT_NOTES: {
      const document = action.itemId
        ? mapItem(record.document, action.itemId, (item) => {
          assertLoggableItem(record, item);
          return { ...item, notes: action.notes ?? null };
        })
        : { ...record.document, notes: action.notes ?? null };
      const next = revised(record, { document, timing: addActivity(record, at) });
      if (!action.itemId) return next;
      // Remembered for this workout, so the draft returns with its exercise.
      const noted = document.items.find((item) => item.itemId === action.itemId);
      return {
        ...next,
        exerciseNotes: rememberExerciseNote(record, noted.exerciseId, noted.notes),
      };
    }

    // Explicit athlete rest controls (§14.5): each real change is a normal
    // revision + activity event (§8.3); a control with nothing to change
    // returns the original record. An Automatic start is never dispatched: it
    // is folded into the anchoring commit (applyRestAnchor).
    case EXECUTION_ACTIONS.START_REST_TIMER:
      assertRestActive(record);
      if (!REST_TIMER_SOURCES.includes(action.source)) {
        throw new TypeError("The rest timer source must be AUTO or MANUAL.");
      }
      // A Manual Play starts from the Play timestamp (`at`), never from the
      // anchor's performedAt or the offer's offeredAt.
      return revised(record, {
        timing: addActivity(record, at),
        restTimer: startRestTimer({
          now: at,
          durationSeconds: action.durationSeconds,
          anchor: action.anchor,
          source: action.source,
        }),
        restOffer: null,
      });

    case EXECUTION_ACTIONS.EXTEND_REST_TIMER: {
      assertRestActive(record);
      const seconds = action.seconds ?? 30;
      if (!Number.isInteger(seconds) || seconds < 1) {
        throw new TypeError("A rest extension must be a positive whole number of seconds.");
      }
      const timer = record.restTimer;
      if (
        !timer ||
        new Date(at).getTime() >= new Date(timer.endsAt).getTime() ||
        timer.durationSeconds >= REST_TIMER_MAX_SECONDS
      ) {
        return record;
      }
      return revised(record, {
        timing: addActivity(record, at),
        restTimer: extendRestTimer(timer, seconds),
      });
    }

    case EXECUTION_ACTIONS.DISMISS_REST_TIMER:
      assertRestActive(record);
      if (!record.restTimer) return record;
      return revised(record, {
        timing: addActivity(record, at),
        restTimer: dismissRestTimer(),
      });

    case EXECUTION_ACTIONS.DISMISS_REST_OFFER:
      assertRestActive(record);
      if (!record.restOffer) return record;
      return revised(record, {
        timing: addActivity(record, at),
        restOffer: null,
      });

    case EXECUTION_ACTIONS.FINISH: {
      if (record.document.status !== "ACTIVE") throw new Error("Only ACTIVE workouts can finish.");
      const finish = resolveFinishTiming({ finishedAt: at, lastActivityAt: record.timing.lastActivityAt });
      const baseTiming = finish.extendActivityToFinish ? addActivity(record, at) : record.timing;
      const frozenDuration = normalizeActiveDuration({
        activeSegments: baseTiming.activeSegments,
        clientStartedAt: baseTiming.clientStartedAt,
        receivedAt: at,
        items: record.document.items,
      });
      const timing = {
        ...baseTiming,
        activeSegments: frozenDuration.effectiveActiveSegments,
      };
      return revised(record, {
        timing,
        restTimer: null,
        restOffer: null,
        document: {
          ...record.document,
          status: "FINISHED",
          finishedAt: finish.finishedAt,
          workoutEndedAt: finish.workoutEndedAt,
          finishTimezone: action.finishTimezone,
          savedAt: null,
          finalizedBy: null,
          discardedAt: null,
        },
      });
    }

    case EXECUTION_ACTIONS.SAVE:
      if (record.document.status !== "FINISHED") throw new Error("Only FINISHED workouts can be saved.");
      return revised(record, {
        restTimer: null,
        restOffer: null,
        document: {
          ...record.document,
          status: "SAVED",
          savedAt: at,
          finalizedBy: action.finalizedBy || "USER",
          discardedAt: null,
        },
      });

    case EXECUTION_ACTIONS.DISCARD:
      if (!["ACTIVE", "FINISHED"].includes(record.document.status)) {
        throw new Error("Only open workouts can be discarded.");
      }
      return revised(record, {
        restTimer: null,
        restOffer: null,
        document: {
          ...record.document,
          status: "DISCARDED",
          savedAt: null,
          finalizedBy: null,
          discardedAt: at,
        },
      });

    case EXECUTION_ACTIONS.CONTINUE_EDITING:
      if (record.document.status !== "FINISHED") {
        throw new Error("Continue Editing keeps a FINISHED workout FINISHED.");
      }
      return revised(record);

    default:
      throw new Error(`Unknown execution action: ${action.type}`);
  }
}
